import type { ReferralResponse, ReferralStatus } from '@clareefai/types';
import { Avatar, toast } from '@clareefai/ui';
import {
  Award,
  ChevronDown,
  Clock,
  Eye,
  Loader2,
  Phone,
  Search,
  ShieldX,
  Star,
  ThumbsDown,
  Users,
  XCircle,
} from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../components/shared/PageTitle';
import {
  useGetVendorReferralsQuery,
  useUpdateReferralStatusMutation,
} from '../../store/vendorApi';

const STATUS_CONFIG: Record<ReferralStatus, { labelKey: string; color: string; icon: React.ElementType }> = {
  PENDING: { labelKey: 'referrals.status.pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  CONTACTED: { labelKey: 'referrals.status.contacted', color: 'bg-blue-100 text-blue-800', icon: Phone },
  QUALIFIED: { labelKey: 'referrals.status.qualified', color: 'bg-green-100 text-green-800', icon: Star },
  DISQUALIFIED: { labelKey: 'referrals.status.disqualified', color: 'bg-red-100 text-red-800', icon: ShieldX },
  REGISTERED: { labelKey: 'referrals.status.registered', color: 'bg-teal-100 text-teal-800', icon: Users },
  WON: { labelKey: 'referrals.status.won', color: 'bg-emerald-100 text-emerald-800', icon: Award },
  LOST: { labelKey: 'referrals.status.lost', color: 'bg-gray-100 text-gray-600', icon: ThumbsDown },
};

const Referrals: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: referrals = [], isLoading, isSuccess, error } = useGetVendorReferralsQuery();
  const [updateStatus] = useUpdateReferralStatusMutation();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<ReferralStatus | 'all'>('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const filtered = referrals.filter((r) => {
    const matchesSearch =
      !searchTerm ||
      `${r.first_name} ${r.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.company || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.promoter_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || r.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: referrals.length,
    pending: referrals.filter((r) => r.status === 'PENDING').length,
    qualified: referrals.filter((r) => r.status === 'QUALIFIED').length,
    won: referrals.filter((r) => r.status === 'WON').length,
  };

  const handleStatusChange = async (id: string, status: ReferralStatus) => {
    setUpdatingId(id);
    try {
      await updateStatus({ id, status }).unwrap();
      toast.success(t('referrals.statusUpdated', { status: t(STATUS_CONFIG[status].labelKey) }));
    } catch (err: any) {
      toast.error(err?.data?.message || t('referrals.statusUpdateFailed'));
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      <PageTitle title={t('referrals.title')} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-purple-600" />
            {t('referrals.title')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('referrals.subtitle')}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="text-sm text-gray-500">{t('referrals.stats.total')}</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="text-sm text-yellow-600">{t('referrals.stats.pending')}</div>
          <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="text-sm text-green-600">{t('referrals.stats.qualified')}</div>
          <div className="text-2xl font-bold text-green-700">{stats.qualified}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border p-4">
          <div className="text-sm text-emerald-600">{t('referrals.stats.won')}</div>
          <div className="text-2xl font-bold text-emerald-700">{stats.won}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('referrals.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as ReferralStatus | 'all')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
          >
            <option value="all">{t('referrals.allStatuses')}</option>
            <option value="PENDING">{t('referrals.status.pending')}</option>
            <option value="CONTACTED">{t('referrals.status.contacted')}</option>
            <option value="QUALIFIED">{t('referrals.status.qualified')}</option>
            <option value="DISQUALIFIED">{t('referrals.status.disqualified')}</option>
            <option value="WON">{t('referrals.status.won')}</option>
            <option value="LOST">{t('referrals.status.lost')}</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-3" />
          <p className="text-gray-500">{t('referrals.loadingReferrals')}</p>
        </div>
      ) : error && !isSuccess ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <XCircle className="w-8 h-8 text-red-400 mx-auto mb-3" />
          <p className="text-gray-500">{t('referrals.failedToLoad')}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">{t('referrals.noReferralsFound')}</h3>
          <p className="text-gray-500">
            {referrals.length === 0
              ? t('referrals.noReferralsDesc')
              : t('referrals.adjustSearch')}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">{t('referrals.columns.prospect')}</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">{t('referrals.columns.company')}</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">{t('referrals.columns.referredBy')}</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">{t('referrals.columns.assignedTo')}</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">{t('referrals.columns.status')}</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">{t('referrals.columns.date')}</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">{t('referrals.columns.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((referral) => {
                  const statusConfig = STATUS_CONFIG[referral.status];
                  const StatusIcon = statusConfig.icon;
                  return (
                    <tr
                      key={referral.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/referrals/${referral.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar
                            name={`${referral.first_name} ${referral.last_name}`}
                            size="sm"
                          />
                          <div>
                            <div className="font-medium text-gray-900 text-sm">
                              {referral.first_name} {referral.last_name}
                            </div>
                            <div className="text-xs text-gray-500">{referral.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {referral.company || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {referral.promoter_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {referral.assigned_to_name || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {t(statusConfig.labelKey)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(referral.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          <StatusDropdown
                            currentStatus={referral.status}
                            onSelect={(status) => handleStatusChange(referral.id, status)}
                            isLoading={updatingId === referral.id}
                          />
                          <button
                            onClick={() => navigate(`/referrals/${referral.id}`)}
                            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title={t('referrals.viewDetails')}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const StatusDropdown: React.FC<{
  currentStatus: ReferralStatus;
  onSelect: (status: ReferralStatus) => void;
  isLoading: boolean;
}> = ({ currentStatus, onSelect, isLoading }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const statuses: ReferralStatus[] = ['PENDING', 'CONTACTED', 'QUALIFIED', 'DISQUALIFIED', 'WON', 'LOST'];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={isLoading}
        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        title={t('referrals.changeStatus')}
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-lg shadow-lg border z-20 py-1">
            {statuses.map((status) => {
              const config = STATUS_CONFIG[status];
              const Icon = config.icon;
              return (
                <button
                  key={status}
                  onClick={() => {
                    onSelect(status);
                    setOpen(false);
                  }}
                  disabled={status === currentStatus}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${status === currentStatus ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {t(config.labelKey)}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default Referrals;
